import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventCard, EventCardData } from '../../components/EventCard';

interface Attendee {
  id: string;
  name: string;
  avatarUrl: string | null;
}

interface PublicRoom {
  id: string;
  code: string;
  name: string;
  description: string | null;
  scheduledAt: string | null;
  endsAt: string | null;
  startedAt: string | null;
  hostName: string;
  hostAvatarUrl: string | null;
  modelName: string | null;
  modelAvatarUrl?: string | null;
  seatsTotal: number | null;
  seatsConfirmed: number;
  isPublic: boolean;
  isTutored: boolean;
  coverImageUrl?: string | null;
  attendees?: Attendee[];
  pricingType?: 'free' | 'fixed' | 'donation';
  ticketPrice?: number | null;
}

// Convert PublicRoom to EventCardData
function toEventCardData(room: PublicRoom): EventCardData {
  return {
    code: room.code,
    name: room.name,
    description: room.description,
    scheduledAt: room.scheduledAt,
    endsAt: room.endsAt,
    startedAt: room.startedAt,
    coverImageUrl: room.coverImageUrl || null,
    hostName: room.hostName,
    hostAvatarUrl: room.hostAvatarUrl,
    modelName: room.modelName,
    modelAvatarUrl: room.modelAvatarUrl || null,
    isTutored: room.isTutored,
    seatsTotal: room.seatsTotal,
    seatsConfirmed: room.seatsConfirmed,
    attendees: room.attendees,
    pricingType: room.pricingType,
    ticketPrice: room.ticketPrice,
  };
}

type FilterType = 'all' | 'live' | 'tutored';

export function EventsSection({ showEmptyState = false }: { showEmptyState?: boolean } = {}) {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const sectionClass = showEmptyState ? 'py-24' : 'py-24 px-8';
  const containerClass = showEmptyState ? 'page-container' : 'max-w-[1240px] mx-auto';
  const titleClass = showEmptyState
    ? 'page-heading'
    : 'font-display font-light text-[clamp(34px,3.8vw,54px)] leading-tight';

  useEffect(() => {
    fetchPublicRooms();
  }, []);

  const fetchPublicRooms = async () => {
    try {
      const response = await fetch('/api/rooms/public');
      if (response.ok) {
        const data = await response.json();
        setRooms(data.rooms);
      }
    } catch (error) {
      console.error('Error fetching public rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRooms = rooms.filter((room) => {
    if (filter === 'live' && !room.startedAt) return false;
    if (filter === 'tutored' && !room.isTutored) return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        room.name.toLowerCase().includes(query) ||
        room.hostName.toLowerCase().includes(query) ||
        (room.modelName && room.modelName.toLowerCase().includes(query))
      );
    }

    return true;
  });

  const groupedRooms = groupRoomsByDay(filteredRooms);

  if (loading) {
    return (
      <section id="sessions" className={sectionClass}>
        <div className={containerClass}>
          <div className="animate-pulse">
            <div className="h-8 bg-line rounded w-48 mb-4" />
            <div className="h-4 bg-line rounded w-full max-w-96 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/3] bg-line rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (rooms.length === 0 && !showEmptyState) {
    return null;
  }

  return (
    <section id="sessions" className={`${sectionClass} bg-bg-secondary border-t border-b border-line-soft`}>
      <div className={containerClass}>
        {/* Header */}
        <div className="mb-10">
          <div className="kicker mb-3">Upcoming · public sessions</div>
          <h2 className={titleClass}>
            Open rooms coming up.
          </h2>
        </div>

        {/* Toolbar: Filters + Search */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <div className="flex gap-1 flex-wrap">
            <FilterPill
              active={filter === 'all'}
              onClick={() => setFilter('all')}
              label="All"
            />
            <FilterPill
              active={filter === 'live'}
              onClick={() => setFilter('live')}
              label="Live now"
              dot
            />
            <FilterPill
              active={filter === 'tutored'}
              onClick={() => setFilter('tutored')}
              label="Tutored"
            />
          </div>
          <div className="flex-1 min-w-[180px] max-w-[320px] flex items-center gap-2 px-3.5 py-2 bg-panel border border-line rounded-full">
            <svg
              className="w-3 h-3 text-text-faint flex-shrink-0"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            >
              <circle cx="5" cy="5" r="3.5" />
              <path d="M7.6 7.6L10.5 10.5" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by host, model, theme..."
              className="flex-1 bg-transparent border-none outline-none text-[12px] text-text placeholder:text-text-faint"
            />
          </div>
        </div>

        {/* Event list */}
        {filteredRooms.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-text-dim">
              {rooms.length === 0 ? 'No drawing events scheduled yet.' : 'No sessions match your search.'}
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {Object.entries(groupedRooms).map(([dayLabel, dayRooms]) => (
              <div key={dayLabel}>
                <div className="flex items-baseline gap-4 mb-5 pb-3 border-b border-line">
                  <h3 className="font-display text-[22px] font-medium">
                    {dayLabel}
                  </h3>
                  <span className="font-mono text-[11px] text-text-dim uppercase">
                    {dayRooms.length} session{dayRooms.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {dayRooms.map((room) => (
                    <EventCard
                      key={room.id}
                      event={toEventCardData(room)}
                      onClick={() => navigate(`/s/${room.code}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FilterPill({
  active,
  onClick,
  label,
  dot,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  dot?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`filter-pill ${active ? 'active' : ''}`}
    >
      {dot && <span className="live-dot" />}
      {label}
    </button>
  );
}

function groupRoomsByDay(rooms: PublicRoom[]): Record<string, PublicRoom[]> {
  const groups: Record<string, PublicRoom[]> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayAfter = new Date(today);
  dayAfter.setDate(dayAfter.getDate() + 2);

  for (const room of rooms) {
    let label: string;

    if (room.startedAt) {
      label = 'Live now';
    } else if (!room.scheduledAt) {
      label = 'Unscheduled';
    } else {
      const roomDate = new Date(room.scheduledAt);
      roomDate.setHours(0, 0, 0, 0);

      if (roomDate.getTime() === today.getTime()) {
        label = 'Today';
      } else if (roomDate.getTime() === tomorrow.getTime()) {
        label = 'Tomorrow';
      } else if (roomDate < dayAfter) {
        label = roomDate.toLocaleDateString('en-US', { weekday: 'long' });
      } else {
        label = roomDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      }
    }

    if (!groups[label]) {
      groups[label] = [];
    }
    groups[label].push(room);
  }

  // Sort groups with "Live now" first
  const sortedGroups: Record<string, PublicRoom[]> = {};
  if (groups['Live now']) {
    sortedGroups['Live now'] = groups['Live now'];
  }
  if (groups['Today']) {
    sortedGroups['Today'] = groups['Today'];
  }
  if (groups['Tomorrow']) {
    sortedGroups['Tomorrow'] = groups['Tomorrow'];
  }
  for (const label of Object.keys(groups)) {
    if (!sortedGroups[label]) {
      sortedGroups[label] = groups[label];
    }
  }

  return sortedGroups;
}
